import React, {
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import { useRouteMatch } from 'react-router-dom';

import useNavigation from '../../../hooks/useNavigation';
import useSessionStore from '../../../hooks/useSessionStore';
import ExternalCoachContext from '../../context/ExternalCoachContext';
import {
  WorkoutsManagerTab,
  workoutsManagerPagesConfig,
  getTabPath,
} from '../../routes/workoutsManager/workoutsManagerPages';
import {
  Container,
  Header,
  StyledTabs,
} from './styles';
import { WorkoutsManagerTabsConfig } from './utils';

const WorkoutsManagerDashboard = () => {
  const { navigateTo } = useNavigation();
  const match = useRouteMatch();
  const { isCoachAssistant, isAdmin } = useSessionStore();
  const { coachDoc } = useContext(ExternalCoachContext);

  const [selectedTab, setSelectedTab] = useState(WorkoutsManagerTab.PROGRAMS);
  /*
    Tracks tab is visible if
    - The coach is a public tracks coach
    - User is AC or admin
  */
  const isTracksVisible = coachDoc.isPublicTracksCoach
    || isCoachAssistant || isAdmin;

  const tabsConfig = useMemo(() => {
    if (isTracksVisible) {
      return WorkoutsManagerTabsConfig;
    }
    const { [WorkoutsManagerTab.TRACKS]: _, ...rest } = WorkoutsManagerTabsConfig;
    return rest;
  }, [isTracksVisible]);

  const navigateToTab = useCallback((tab, options = {}) => (
    navigateTo(getTabPath(tab), options)
  ), [navigateTo]);

  const onSelectedTab = useCallback((tabSelected) => {
    navigateToTab(tabSelected, { clearSearch: true });
  }, [
    navigateToTab,
  ]);

  useEffect(() => {
    // Get the last part of the pathname to detect if it is a valid workouts manager tab.
    const currentWorkoutsManagerTab = match.path.split('/').pop();

    // Get the config object from the path we extracted from the url
    const tabConfig = Object.values(workoutsManagerPagesConfig)
      .find(({ path }) => path === currentWorkoutsManagerTab);

    if (tabConfig) {
      if (tabConfig.id === WorkoutsManagerTab.TRACKS && !isTracksVisible) {
        navigateToTab(WorkoutsManagerTab.PROGRAMS, { clearSearch: true });
        return;
      }
      setSelectedTab(tabConfig.id);
    }
  }, [match, isTracksVisible, navigateToTab]);

  const tabContent = useMemo(() => {
    const { component: TabContent } = tabsConfig[selectedTab] || tabsConfig[WorkoutsManagerTab.PROGRAMS];
    return (
      <TabContent />
    );
  }, [selectedTab, tabsConfig]);

  return (
    <Container>
      <Header>
        <StyledTabs
          tabsConfig={tabsConfig}
          selectedTab={selectedTab}
          onSelectTab={onSelectedTab}
        />
      </Header>
      {tabContent}
    </Container>
  );
};

export default compose(
  observer,
)(WorkoutsManagerDashboard);
